import React, { useState, useEffect } from "react";
import { X, Loader2, PackageCheck, Flag } from "lucide-react";

export default function ReceiveBatchModal({
  isOpen,
  onClose,
  shipment,
  itemsToReceive,
  zones,
  loading,
  onSubmit
}) {
  const [receivingItems, setReceivingItems] = useState([]);

  useEffect(() => {
    if (itemsToReceive && itemsToReceive.length > 0) {
      setReceivingItems(
        itemsToReceive.map(item => ({
          item,
          vendorKey: item.vendorKey || item.id,
          arrived: true,
          receivedQuantity: item.vendorQty !== undefined ? item.vendorQty : (item.quantity || 0),
          totalQuantity: item.vendorQty !== undefined ? item.vendorQty : (item.quantity || 0),
          isFlagged: false,
          flagReason: "",
          zone: "",
          notes: ""
        }))
      );
    }
  }, [itemsToReceive]);

  if (!isOpen || !itemsToReceive || itemsToReceive.length === 0) return null;

  const handleUpdateItem = (index, field, value) => {
    setReceivingItems(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      if (field === "arrived" && !value) {
        copy[index].receivedQuantity = 0;
      } else if (field === "arrived" && value) {
        copy[index].receivedQuantity = copy[index].totalQuantity;
      }
      return copy;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(receivingItems);
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-content" style={{ maxWidth: "800px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <PackageCheck size={22} color="var(--success)" />
            <h3 style={{ margin: 0 }}>Receive Items to Warehouse</h3>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", margin: "0.5rem 0 1rem 0" }}>
          The following {receivingItems.length} item(s) on PO <strong>{shipment?.poNumber}</strong> are progressing into <strong>Warehouse</strong> status. Specify receiving details and storage zones below:
        </p>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto", paddingRight: "0.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
            {receivingItems.map((rcv, idx) => {
              const item = rcv.item;
              return (
                <div 
                  key={rcv.vendorKey || idx}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    padding: "1rem"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
                        {item.lineNumber ? `${item.lineNumber} - ` : ""}{item.description || item.name || "Item"}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>
                        Vendor: <strong>{item.vendorName || item.vendor || "—"}</strong> • Qty: <strong>{rcv.totalQuantity}</strong>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, color: rcv.arrived ? "var(--success)" : "var(--text-muted)" }}>
                        <input
                          type="checkbox"
                          checked={rcv.arrived}
                          onChange={(e) => handleUpdateItem(idx, "arrived", e.target.checked)}
                          style={{ width: "16px", height: "16px", accentColor: "var(--success)", cursor: "pointer" }}
                        />
                        {rcv.arrived ? "Arrived" : "Not Arrived"}
                      </label>
                    </div>
                  </div>

                  {rcv.arrived && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 2fr", gap: "0.75rem", alignItems: "flex-end" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}>
                          Qty Received ({rcv.totalQuantity})
                        </label>
                        <input
                          type="number"
                          className="form-input"
                          min="0"
                          max={rcv.totalQuantity}
                          value={rcv.receivedQuantity}
                          onChange={(e) => handleUpdateItem(idx, "receivedQuantity", e.target.value === "" ? 0 : Number(e.target.value))}
                          style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem" }}
                          required
                        />
                      </div>

                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}>
                          Route To Zone *
                        </label>
                        <select
                          className="form-input"
                          value={rcv.zone}
                          onChange={(e) => handleUpdateItem(idx, "zone", e.target.value)}
                          style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem", backgroundColor: "var(--bg-input)" }}
                          required
                        >
                          <option value="" disabled>Select Zone...</option>
                          {zones && zones.filter(z => !z.isOutline).map(z => (
                            <option key={z.id} value={z.name}>{z.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}>
                          Receiving Notes
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Optional notes..."
                          value={rcv.notes}
                          onChange={(e) => handleUpdateItem(idx, "notes", e.target.value)}
                          style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid var(--border-color)" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{ backgroundColor: "var(--success)", borderColor: "var(--success)", color: "#000000" }} disabled={loading}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : null}
              Receive All & Advance
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
